/**
 * modeAudio.js — single source of truth for Clear / Soft / Deep volumes.
 *
 * User requirement (Michael, May 2026):
 *   Clear  → "they can hear the words"        (full voice ~0.90)
 *   Soft   → "just hear them, but hard to     (mid voice   ~0.38)
 *             ascertain exactly what is said"
 *   Deep   → "just can hear a whisper but     (low voice   ~0.14)
 *             not enough to be understood"
 *
 * Music + binaural are kept close to constant (they carry the state);
 * only the VOICE layer changes with the mode, because the mode choice
 * is about how much of the affirmation the conscious mind gets to hear.
 *
 * Every audio player in the app reads from this util.
 */
export const MODE_VOICE_VOLUMES = {
  clear: 0.90,
  soft:  0.38,
  deep:  0.14,
};

export const MODE_MUSIC_VOLUMES = {
  // Gentle variation so the mode has a full *felt* difference.
  // Clear ≈ slightly lower music so the voice sits above it.
  // Deep  ≈ slightly louder music so it carries the subliminal voice.
  clear: 0.12,
  soft:  0.18,
  deep:  0.24,
};

export const MODE_BINAURAL_VOLUMES = {
  clear: 0.14,
  soft:  0.20,
  deep:  0.26,
};

const isMode = (m) => m === "clear" || m === "soft" || m === "deep";

/** Voice (affirmations / guidance) volume for a given mode. */
export function voiceVolume(mode) {
  return MODE_VOICE_VOLUMES[isMode(mode) ? mode : "soft"];
}

/** Background music volume for a given mode. */
export function musicVolume(mode) {
  return MODE_MUSIC_VOLUMES[isMode(mode) ? mode : "soft"];
}

/** Binaural / frequency-tone layer volume for a given mode. */
export function binauralVolume(mode) {
  return MODE_BINAURAL_VOLUMES[isMode(mode) ? mode : "soft"];
}

/**
 * Convenience — read the current mode from localStorage without needing
 * the React hook (useful inside non-component audio players).
 */
export function getStoredAudioMode() {
  try {
    const m = localStorage.getItem("reclaim_audio_mode");
    return isMode(m) ? m : "soft";
  } catch (_) {
    return "soft";
  }
}
